/**
 * Navigation Widget
 * Extends generic navigation
 * @author yauheni.mukavozchyk@isobar.com
 */

import { findTabbableDescendants } from 'utils/accessibility/tabbable';
import { i18n } from 'utils/i18n';
import { config } from 'utils/config';
import { analyticsInteraction } from 'utils/analytics';
import { ANALYTICS } from 'constants';
import RESERVATIONS from 'constants/reservations';
import { setAriaDrawerTags } from 'utils/accessibility/aria';
import initGenericNavigation, { findMenuAndSubMenuEls } from './shared/genericNavigation';

/**
 * Constants
 */
const SKIP_LINK_ID = 'primary-nav-skip-link';
const LANGUAGE_HELP_TOGGLE_BUTTON_ID = ['help__toggle__button', 'language__toggle__button'];

/**
 * Flag Username of Member number
 */
const isMemberNumber = config.getEnableMemberNumberText();

/**
 * Finds last tabbable element in nav and focuses next one
 * @param {event} e
 */
const createOnSkipLink = (domEl) => (e) => {
  e.preventDefault();
  const allTabbableEls = findTabbableDescendants(document.body);
  const navTabbableEls = findTabbableDescendants(domEl);

  const lastNavTabbableEl = navTabbableEls.pop();
  const globalIndexOfLastNavTabbableEl = allTabbableEls.indexOf(lastNavTabbableEl);

  if (globalIndexOfLastNavTabbableEl !== allTabbableEls.length - 1) {
    allTabbableEls[globalIndexOfLastNavTabbableEl + 1].focus();
  }

  const isHamburgerMenu = window.innerWidth <= 1280;

  // Should close sidebar menu
  const headerNavigationElem = document.getElementById('header-navigation');
  if (isHamburgerMenu && headerNavigationElem.getAttribute('aria-hidden') === 'false') {
    setAriaDrawerTags(headerNavigationElem, false);
  }
};

/**
 * Attaches event handlers specific to primary nav
 * @param {DOMElement} domEl
 */
const attachPrimaryNavHandlers = (domEl) => {
  const skipLink = document.getElementById(SKIP_LINK_ID);
  skipLink && skipLink.addEventListener('click', createOnSkipLink(domEl));

  domEl.addEventListener('click', (e) => {
    const { menuEl } = findMenuAndSubMenuEls(e.srcElement);
    const isSubMenuOpen = menuEl.getAttribute('aria-expanded') === 'true';
    const isHeaderControls =
      e.srcElement.parentElement?.className.length > 0 && e.srcElement.parentElement?.className === 'header__controls';
    const isProfileMenuOpen = e?.target?.className === 'header-flyout__top__name';

    if (isSubMenuOpen) {
      if (isProfileMenuOpen) {
        menuEl.setAttribute('aria-expanded', false);
        menuEl.parentElement?.nextElementSibling?.setAttribute('aria-hidden', true);
      } else if (!isHeaderControls && e.srcElement.innerText.length > 0 && !e.srcElement.innerText.includes('\n')) {
        analyticsInteraction('menu', 'nav', menuEl.innerText.toLowerCase(), e.srcElement.innerText.toLowerCase());
      }
    } else {
      const helpToggleButton = e.srcElement.closest('nav');
      helpToggleButton?.firstElementChild?.ariaExpanded === true;
      if (
        helpToggleButton &&
        helpToggleButton.classList.contains('expanded') &&
        LANGUAGE_HELP_TOGGLE_BUTTON_ID.includes(helpToggleButton?.firstElementChild?.className)
      ) {
        const value = helpToggleButton.firstElementChild?.innerText;
        analyticsInteraction('menu', 'nav', value.toLowerCase(), e.srcElement.innerText.toLowerCase());

        // Storing the language in session storage
        if (helpToggleButton.firstElementChild.className === LANGUAGE_HELP_TOGGLE_BUTTON_ID[1]) {
          const language = JSON.parse(sessionStorage.getItem(RESERVATIONS.SELECTED_LANGUGAGE_SESSION_STORAGE));
          const payload = {
            prevLang: language?.prevLang ? language.language : null,
            currLang: e.srcElement.innerText.toLowerCase(),
          };
          sessionStorage.setItem(RESERVATIONS.SELECTED_LANGUGAGE_SESSION_STORAGE, JSON.stringify(payload));
        }
      } else {
        // Handle sign-in flyout and Main nav interaction
        const primaryMainNav = !!e.srcElement.closest('.navigation__container .navigation__primary');

        const isValidSignInClick =
          !isHeaderControls &&
          e.srcElement.innerText.length > 0 &&
          ![
            'header-flyout__top__links',
            'header-flyout__top__toggle__content',
            'signin__form',
            'header-flyout__list-item',
            'header-flyout header-flyout--unauthenticated',
          ].includes(e.srcElement.parentElement.className) &&
          !primaryMainNav;

        if (isValidSignInClick) {
          let dataValue;
          const buttonText = e.srcElement.innerText;
          const keyName = i18n('signin_forgot_member_number_text');
          const keyPasswordtext = i18n('signin_forgot_password_text');
          // conditionally converting text to lowercase
          if (isMemberNumber && buttonText.includes(keyName)) {
            dataValue = ANALYTICS.FORGOT_MEMBER_NUMBER;
          } else {
            dataValue = buttonText.includes(keyPasswordtext) ? ANALYTICS.FORGOT_PASSWORD : buttonText?.toLowerCase();
          }
          // Analytics for sign-in flyout
          analyticsInteraction('menu', 'nav', i18n('signin_flyout_toggle').toLowerCase(), dataValue);
        } else {
          // Analytics for primary nav
          primaryMainNav && analyticsInteraction('menu', 'nav', e.srcElement.innerText.toLowerCase());
        }
      }
    }
  });
};
const docRead = (fn) => {
  if (document.readyState === 'complete' || document.readyState === 'interactive') setTimeout(fn, 0);
  else document.addEventListener('DOMContentLoaded', fn);
};
/**
 * Entry point of primary navigation widget
 */
export default {
  init(store, domEl) {
    docRead(() => {
      const isVerticalNav = window.innerWidth < 1280;
      // initialize generic navigation
      initGenericNavigation({ domEl, isVerticalNav });

      // add primary nav specific handlers
      attachPrimaryNavHandlers(domEl);
    });
  },
};
